import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcDtcFilesV1FileRequestQueryServiceFileRequestGet } from '@interxfi/api-client/generated/hooks/exc-dtc-files-v1-file-request-query-service-file-request-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcDtcFilesV1FileRequestState } from '@interxfi/types';
import { DtcFileRequestEventDrawer, DtcFileRequestEventSummary } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-request-event';
import { getDtcFileRequestStateListItems } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-request-list-items';
import { getDtcFileRequestDetailPath } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-request-routing';
import { DtcFileRequestStatusPill } from '../../../../../../../../../../../features/dtc/files/v1/dtc-file-request-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/dtcc/_dtcc/file-request/_file-request/$fileRequestId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `dtc-file-request-${params.fileRequestId}`,
        label: 'DTC File Request',
        to: getDtcFileRequestDetailPath(params.fileRequestId),
        suffix: (
          <CopyButton
            content={params.fileRequestId}
            label="Copy DTC File Request ID"
            tooltip={{ copy: params.fileRequestId, copied: 'Copied DTC File Request ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('DTC File Request'),
  component: DtcFileRequestDetail,
});

function getListItems(data: ExcDtcFilesV1FileRequestState | undefined) {
  const listItems = getDtcFileRequestStateListItems(data);

  return {
    generalLeft: [listItems.data.spec.requestFilePath, listItems.data.spec.responseFilePath] satisfies ReadonlyListItem[],
    generalRight: [listItems.fileRequestId, listItems.data.spec.fileId] satisfies ReadonlyListItem[],
  };
}

function getMenuItems(fileRequestId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DtcFileRequestEventDrawer,
        props: { request: { fileRequestId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load DTC file request', 'view this DTC file request');

function DtcFileRequestDetail() {
  const { fileRequestId } = Route.useParams();
  const request = { fileRequestId } as const;
  const { data, error, isPending } = useExcDtcFilesV1FileRequestQueryServiceFileRequestGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.fileRequest), [data?.fileRequest]);
  const menuItems = useMemo(() => getMenuItems(fileRequestId), [fileRequestId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <DtcFileRequestStatusPill isPending={showPending} size="large" status={data?.fileRequest?.status} />
            </HorizontalStack>
            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <DtcFileRequestEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.generalLeft} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.generalRight} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
